import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiAuthError } from './lib/api';
import { applyOrgBranding, extractOrgBranding } from './lib/branding';
import { AppLayout, SidebarSection, SidebarUser } from './shell';
import { Button } from './atoms';
import { useToast } from './organisms';

/**
 * App — CoreOS layout wrapper with sidebar navigation.
 *
 * Sidebar sections:
 *   - Dashboard
 *   - CRM: Accounts, Contacts, Relationships
 *   - Operations: Work Orders, Work Types, Assets, Service Resources
 *   - Configuration: Settings
 *   - System: Audit Log, Users
 */

interface CurrentUserResponse {
  id: string;
  email: string;
  role: string;
  name?: string;
  org_name?: string;
  brand_primary_color?: string | null;
  brand_secondary_color?: string | null;
  brand_accent_color?: string | null;
}

const sidebarSections: SidebarSection[] = [
  {
    items: [
      { to: '/', label: 'Dashboard', end: true },
    ],
  },
  {
    label: 'CRM',
    items: [
      { to: '/accounts', label: 'Accounts' },
      { to: '/contacts', label: 'Contacts' },
      { to: '/relationships', label: 'Relationships' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/work-orders', label: 'Work Orders' },
      { to: '/work-types', label: 'Work Types' },
      { to: '/assets', label: 'Assets' },
      { to: '/service-resources', label: 'Service Resources' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { to: '/settings', label: 'Settings' },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/audit-log', label: 'Audit Log' },
      { to: '/users', label: 'Users' },
    ],
  },
];

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<SidebarUser | undefined>();

  const currentUserQuery = useQuery<CurrentUserResponse>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUserResponse>('/api/current-user', signal),
    retry: false,
  });

  useEffect(() => {
    if (currentUserQuery.data) {
      const u = currentUserQuery.data;
      setUser({
        name: u.name || u.email,
        email: u.email,
        role: u.role,
      });
      const branding = extractOrgBranding(u);
      applyOrgBranding(branding);
    }
  }, [currentUserQuery.data]);

  useEffect(() => {
    if (currentUserQuery.error instanceof ApiAuthError) {
      navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }, [currentUserQuery.error, navigate, location.pathname]);

  // Generate breadcrumbs from URL path
  const breadcrumbs = breadcrumbsForPath(location.pathname);

  const handleSignOut = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
      toast({ variant: 'info', message: 'Signed out.' });
      navigate('/login', { replace: true });
    } catch {
      navigate('/login', { replace: true });
    }
  };

  return (
    <AppLayout
      sidebar={{
        sections: sidebarSections,
        user,
        brandName: currentUserQuery.data?.org_name || 'CoreOS',
        brandTagline: 'Platform',
      }}
      topbar={{
        breadcrumbs,
        actions: (
          <Button variant="ghost" size="sm" onClick={handleSignOut}>
            Sign out
          </Button>
        ),
      }}
    >
      <Outlet />
    </AppLayout>
  );
}

function breadcrumbsForPath(path: string) {
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return [{ label: 'Dashboard' }];
  return [
    { label: 'Home', to: '/' },
    ...segments.map((seg, i) => {
      const isLast = i === segments.length - 1;
      const label = seg
        .replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      return {
        label,
        to: isLast ? undefined : '/' + segments.slice(0, i + 1).join('/'),
      };
    }),
  ];
}
